/*----------------------------------
- TLD APPLICATIONS ROUTE
----------------------------------*/

/**
 * @docs docs/features/tld-applications
 * @adr  ADR-0055
 * @adr  ADR-0065
 * @rule Path pagination only (`/tld/applications`, `/tld/applications/page/:page`): a page past
 *       the last answers NotFound, `/page/1` 301s to the base path, each page is its own
 *       canonical with rel prev and next. The query string is never read.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { NotFound } from '@common/errors';
import type ApiClient from '@common/router/request/api';
import type { TFetcher } from '@common/router/request/api';
import {
    TLD_APPLICATIONS_PATH,
    TLD_HUB_PATH,
    tldApplicationsPagePath,
    type PublicTldApplications,
} from '@uniquedomains/components/contracts/tlds/public';
import { TLD_APPLICATIONS_PAGE_MIRROR_PATH } from '@uniquedomains/core/domains/tlds';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { TLD_PAGE_CONTENT } from '@/client/catalogs/landing/tld/pageContent';
import {
    PUBLIC_REVALIDATE_HEADERS,
    PublicPageUnavailable,
    setPublicUnavailableHeaders,
} from '@/client/pages/landing/lib/publicUnavailable';
import { buildTldApplicationsStructuredData, type WebsiteJsonLdNode } from '@/client/pages/landing/seo/structuredData';
import { PUBLIC_INDEX_ROBOTS_DIRECTIVES } from '@/common/catalogs/crawlerPolicy';
import type { PublicReadResult } from '@/common/contracts/publicReadResult';
import { TLD_APPLICATIONS_GATEWAY_PATH } from '@/common/contracts/tldApplicationsGateway';
import { cachedLandingHeaderNavigation } from '@/common/libs/connected/publicProductReadCache';
import TldApplicationsPage from '../page/applications/TldApplicationsPage';
import { isTldApplicationsFirstPageAlias, resolveTldApplicationsPage } from './applicationsPaging';

/*----------------------------------
- TYPES
----------------------------------*/

type ApplicationsRouteRequest = {
    url: string;
    res?: {
        setHeader: (name: string, value: string) => void;
    };
};

type ApplicationsRouteDataContext = {
    Product: {
        Navigation: {
            GetLandingHeaderNavigation: () => TFetcher<LandingHeaderDomainsNavigation>;
        };
    };
    api: Pick<ApiClient, 'createFetcher'>;
    request: ApplicationsRouteRequest;
};

type ApplicationsPageDocument = {
    description?: string;
    head: Array<{ $: string; [key: string]: string | Date | null | undefined }>;
    jsonld: WebsiteJsonLdNode[];
    metas: Record<string, string | Date | null | undefined>;
    title?: string;
    url: string;
};

type ApplicationsRouteRenderContext = {
    requestedPage: number;
    /** The request's clock, read once in `data` so the server render and the hydration agree. */
    nowIso: string;
    applicationsResult: PublicReadResult<PublicTldApplications> | null;
    navigation: LandingHeaderDomainsNavigation | null;
    page: ApplicationsPageDocument;
    request: ApplicationsRouteRequest;
    response?: {
        canonicalUrl?: URL;
        redirect?: (url: string, code?: number) => void;
        setHeaders?: (headers: Record<string, string>) => void;
    };
    Router: { url: (path: string) => string };
};

/*----------------------------------
- ROUTE
----------------------------------*/

export const buildTldApplicationsRouteData = ({ Product, api, request }: ApplicationsRouteDataContext) => {
    const requestedPage = resolveTldApplicationsPage(request.url);
    if (requestedPage === null) throw new NotFound();

    // `/page/1` is the base page under a second URL: redirected in render, without reading.
    if (isTldApplicationsFirstPageAlias(request.url)) return { requestedPage, nowIso: new Date().toISOString(), applicationsResult: null, navigation: null };

    return {
        requestedPage,
        nowIso: new Date().toISOString(),
        applicationsResult: api.createFetcher<PublicReadResult<PublicTldApplications>>('POST', TLD_APPLICATIONS_GATEWAY_PATH, {
            page: requestedPage,
        }),
        navigation: cachedLandingHeaderNavigation.read() ?? Product.Navigation.GetLandingHeaderNavigation(),
    };
};

export const renderTldApplicationsRoute = ({
    requestedPage,
    nowIso,
    applicationsResult,
    navigation,
    page,
    request,
    response,
    Router,
}: ApplicationsRouteRenderContext) => {
    if (!applicationsResult) {
        response?.redirect?.(Router.url(TLD_APPLICATIONS_PATH), 301);
        return null;
    }
    if (applicationsResult.status === 'unavailable') {
        setPublicUnavailableHeaders(request, response);
        throw new PublicPageUnavailable(TLD_PAGE_CONTENT.applications.unavailable);
    }

    const applications = applicationsResult.value;
    // Product clamps a page past the last to the last one: the page asked for is then not the page answered.
    if (applications.page !== requestedPage || requestedPage > applications.totalPages) throw new NotFound();

    const content = TLD_PAGE_CONTENT.applications;
    const canonicalUrl = Router.url(tldApplicationsPagePath(requestedPage));
    const title = requestedPage > 1 ? content.meta.pageTitleWithPage(requestedPage) : content.meta.pageTitle;
    const description = content.meta.metaDescription;

    page.title = title;
    page.description = description;
    page.url = canonicalUrl;
    page.metas = {
        ...page.metas,
        'og:description': description,
        'og:title': title,
        'og:type': 'website',
        'og:url': canonicalUrl,
        'twitter:card': 'summary_large_image',
        'twitter:description': description,
        'twitter:title': title,
        'twitter:url': canonicalUrl,
    };
    page.head.push({
        $: 'link',
        rel: 'alternate',
        type: 'text/markdown',
        title: content.meta.markdownTitle,
        href: Router.url(TLD_APPLICATIONS_PAGE_MIRROR_PATH),
    });
    page.head.push({ $: 'meta', name: 'robots', content: PUBLIC_INDEX_ROBOTS_DIRECTIVES });
    if (requestedPage > 1) page.head.push({ $: 'link', rel: 'prev', href: Router.url(tldApplicationsPagePath(requestedPage - 1)) });
    if (requestedPage < applications.totalPages) {
        page.head.push({ $: 'link', rel: 'next', href: Router.url(tldApplicationsPagePath(requestedPage + 1)) });
    }
    page.jsonld.push(
        ...buildTldApplicationsStructuredData({
            canonicalUrl,
            description,
            hubUrl: Router.url(TLD_HUB_PATH),
            rows: applications.rows,
            title,
        }),
    );

    if (response) response.canonicalUrl = new URL(canonicalUrl);
    // Revalidated on every hit, so the Reveal Day import shows at once.
    response?.setHeaders?.(PUBLIC_REVALIDATE_HEADERS);

    return <TldApplicationsPage applications={applications} today={nowIso.slice(0, 10)} navigation={navigation} />;
};
